// Pure helpers for the Prepare & Send screen — geometry, signer
// colors, field defaults. No React, no DOM, no fetch, so the node-pool
// tests cover the coordinate math that MUST be right (a wrong
// conversion here puts a signature box on the wrong spot of a client's
// contract).
//
// Coordinate spaces, once:
//   * FIELD space — PDF points, top-left origin, y down, 1-based pages
//     (shared/esign/field-types.ts; what the API stores and sends).
//   * SCREEN space — CSS pixels inside a page's overlay div, whose CSS
//     size is (pagePt × displayScale). Conversion is a single uniform
//     scale factor both ways; the overlay div's top-left IS the page's
//     top-left, so no origin shift is involved.

import type { EsignFieldType } from "../../../../shared/esign/field-types";
import type { EsignTextStyle } from "@shared/esign/text-style";
import { isPreparerField } from "../../../../shared/esign/field-types";
import type { SuggestionSource } from "../../../../shared/esign/field-suggestions";
import { matchCaptionToRole, type SigningRole } from "@shared/esign/signing-roles";

export interface PrepField {
  /** Client-side handle (crypto.randomUUID) — the server mints its own
   *  ids at send time; this one only keys React state. */
  key: string;
  /** WHICH SIGNER this field belongs to — see EsignPlacedField.signerKey.
   *  A contact id when the signer is one of our people, and a
   *  screen-lifetime key when they are not (a co-op agent, a seller, a
   *  title officer). Named for what it is, because a signer who is not
   *  a contact must not be filed under a field called contactId. */
  signerKey: string;
  /** WHOSE NAME A LOCKED BOX PRINTS (Baker, 2026-09-16).
   *
   *  A locked box belongs to the preparer and so names no signer. When
   *  it is bound to a person's field — the buyer's legal name, printed
   *  but not editable by them — this is the seat whose occupant it
   *  reads. Null on every other field. See bindingPersonSignerKey. */
  roleKey?: string | null;
  fieldType: EsignFieldType;
  /** A line drawn through printed text (toolbar program PR 4a): a sender-owned
   *  text box that prints a line, not a value. Per send; never saved into a
   *  layout. See EsignPlacedField.strike. */
  strike?: boolean | null;
  pageNumber: number;
  /** PDF points, top-left origin. */
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
  value: string | null;
  /** Lets the signer change a value we filled in (Baker, 2026-09-18).
   *
   *  The default is the opposite: a text field that goes out with a
   *  value PRINTS and cannot be typed over, which is the fix for Baker
   *  retyping his own name on a real send. This is the opt-out for the
   *  rare blank where the sender's text is a head start rather than a
   *  fact. Undefined and false mean the same thing — protected. */
  signerMayEdit?: boolean;
  /** Points — what this field's value prints at, read off the type
   *  around the blank when the document was mapped. Undefined on a
   *  hand-placed box; the send falls back to the forms' own body size
   *  rather than to the signing service's web-form default. */
  fontSize?: number;
  /** A TEXT BOX SPANNING SEVERAL PRINTED LINES (Grant, 2026-09-29). The
   *  points between its rules is the one number the screen, the stamper
   *  and the detector all derive line positions from — see
   *  shared/esign/multiline-text.ts; `firstLineIndent` is how far in the
   *  first line starts under a printed caption. Undefined on every
   *  single-line box. */
  multiline?: boolean;
  linePitch?: number;
  firstLineIndent?: number;
  /** How a locked text box's type looks: bold, italic, underline, color,
   *  size, indent (Nate, 2026-10-26). Absent is plain. Only the sender's
   *  own box prints it. See shared/esign/text-style.ts. */
  textStyle?: EsignTextStyle;
  /** The record field this text field pulls its value from, when bound
   *  (Grant, 2026-08-22 — merge fields). Null on a plain field, which is
   *  the common case. The BINDING travels and is remembered; the value it
   *  resolves to is computed at send time, so a document reused next
   *  month prints next month's numbers. */
  mergeToken?: string | null;
  /** Pick-one field only: the choices offered, in order. A pick-one with
   *  an empty list is rejected at send — an empty dropdown is a control
   *  the signer cannot satisfy. */
  options?: string[] | null;
  /** True when the OS placed this over one of the SENDER'S OWN
   *  date-signed lines, from a map that knows its seats (2026-09-16).
   *  Only these are cleared and re-placed when the suggestion pass runs
   *  a second time on the same document; a box the advisor placed is
   *  theirs and stays. Never sent — the send payload lists what it
   *  sends, field by field. */
  fromSeat?: boolean;
  /** Choose-one button only: the group it belongs to. Buttons sharing a
   *  group rule each other out, which is the entire point of the type —
   *  so an ungrouped one is rejected at send too. */
  groupName?: string | null;
  /** HOW THIS BLANK GETS FILLED (2026-09-15) — see
   *  shared/esign/fill-rules.ts. Carried from the saved map so the
   *  "only what you know" list can tell a blank the OS answers from one
   *  only the sender can. Null on a hand-placed field and on every map
   *  taught before fill rules existed; null means nobody has said, and
   *  such a field is not asked about. */
  fillRule?: string | null;
  /** The words printed beside this blank on the form, when the map
   *  remembered them. What turns a question into one somebody can
   *  answer: "Full purchase price" against "a box on page 1". */
  caption?: string | null;
  /** WHAT THE OFFER SAYS FOR THIS BOX (Nate, 2026-11-03), when the box is
   *  one a merge field cannot fill: the SCA percentage over the firm
   *  default, or a ticked HOA "paid by" box. `replaces` is the value the
   *  map carried, so accepting takes the offer's value while a value the
   *  sender typed over it stays theirs. Set by the send route for this
   *  send only; never saved into a map. */
  term?: {
    value: string;
    label: string;
    replaces: string | null;
    /** Where the binder got it: "offer" (the typed terms), "house" (the
     *  facts), "package" (the forms in the envelope), "deal" (the side).
     *  The word the ghost's tag shows (ghost-offers.ts). */
    source?: "offer" | "house" | "package" | "deal";
  };
  /** Set while this field is a PROPOSAL — placed by a suggestion rung,
   *  not by the advisor (Grant, 2026-08-22). A proposed field renders
   *  dashed and says where it came from; accepting it clears this and it
   *  becomes an ordinary placed field. Nothing is ever SENT proposed:
   *  the send path only sees accepted fields.
   *
   *  Undefined on every hand-placed field, which is the common case and
   *  keeps the existing shape byte-identical. */
  proposal?: {
    source: SuggestionSource;
    confidence: number;
    /** Plain English, shown on the chip: "Signature line on page 2". */
    reason: string;
  };
  /** WHERE AN ACCEPTED FIELD CAME FROM (2026-09-29): the proposal it
   *  was, kept when it was accepted so Backspace can put it back to a
   *  ghost instead of deleting it. Absent on a hand-placed field, which
   *  has no ghost to return to. Never sent — the payload lists what it
   *  sends key by key. */
  /** WHAT ANOTHER DOCUMENT IN THE PACKAGE SAYS (2026-09-30). Attached by
   *  the package reader to a blank nothing else answered; offered as a
   *  guess with the document's name as its tag. Accepting copies the
   *  value onto the field; Backspace takes it back off. Never sent: the
   *  payload lists what it sends key by key. */
  reading?: {
    value: string;
    documentId: string;
    documentName: string;
    spanId: string;
  } | null;
  origin?: {
    source: SuggestionSource;
    confidence: number;
    reason: string;
  };
}

/** Signer color assignment: stable by position in the record's people
 *  list, reusing the OS's existing mode hues (Opportunity blue,
 *  Processing amber, Care green) plus the brand navy for a fourth —
 *  the same colors the team already reads elsewhere, never new ones. */
export interface SignerColor {
  /** Border / accent. */
  base: string;
  /** Text on the tinted background. */
  deep: string;
  /** Field fill (low-alpha of base). */
  fill: string;
  /** Chip background (the soft mode tint). */
  soft: string;
}

const SIGNER_COLORS: readonly SignerColor[] = [
  { base: "#4e98c6", deep: "#2a6da8", fill: "rgba(78, 152, 198, 0.12)", soft: "#deeef7" }, // mode-opp blue
  { base: "#c69220", deep: "#8a5a16", fill: "rgba(198, 146, 32, 0.12)", soft: "#fdf0d1" }, // mode-proc amber
  { base: "#2a8e6e", deep: "#1f6a52", fill: "rgba(42, 142, 110, 0.12)", soft: "#dcefe8" }, // mode-care green
  { base: "#022c51", deep: "#022c51", fill: "rgba(2, 44, 81, 0.10)", soft: "#e8eef4" }, // brand navy
];

/** Buyers / Sellers 3 and 4 (2026-09-23). Outside the four-colour
 *  rotation on purpose: adding them to it would re-colour the fifth and
 *  sixth signer on every existing send. Violet and brick sit apart from
 *  blue, amber, green and navy at a glance. */
export const THIRD_SEAT_COLOR: SignerColor = {
  base: "#7a5ea8",
  deep: "#563f80",
  fill: "rgba(122, 94, 168, 0.12)",
  soft: "#ece6f5",
};
export const FOURTH_SEAT_COLOR: SignerColor = {
  base: "#b5534a",
  deep: "#8a3a33",
  fill: "rgba(181, 83, 74, 0.12)",
  soft: "#f7e3e0",
};

/** The preparer's own locked boxes. Deliberately OUTSIDE the signer
 *  rotation and deliberately grey: every other colour on the page means
 *  "a person has to do something here", and these are the boxes where
 *  nobody does. A slate box reads as already-settled at a glance, which
 *  is what it is. */
export const PREPARER_COLOR: SignerColor = {
  base: "#6b7785",
  deep: "#3f4854",
  fill: "rgba(107, 119, 133, 0.10)",
  soft: "#eceff3",
};

export function signerColor(index: number): SignerColor {
  const color = SIGNER_COLORS[index % SIGNER_COLORS.length];
  // Length is a positive constant, so the modulo is always in range —
  // the fallback only satisfies noUncheckedIndexedAccess.
  return color ?? SIGNER_COLORS[0]!;
}

/** The group a newly placed choose-one button should join: the group of
 *  the last one placed for the same signer, or a fresh one when this is
 *  their first. Naming is "Choice 1", "Choice 2", … so the inspector has
 *  something readable to show without asking the advisor to invent a
 *  name before they can place a button. */
export function lastRadioGroupFor(fields: readonly PrepField[], signerKey: string): string {
  const theirs = fields.filter((f) => f.fieldType === "radio" && f.signerKey === signerKey);
  const last = theirs[theirs.length - 1];
  if (last?.groupName) return last.groupName;
  // First button for this signer — number it past any group already in
  // use anywhere on the document, so two signers' groups never collide.
  const used = new Set(
    fields
      .filter((f) => f.fieldType === "radio" && f.groupName)
      .map((f) => f.groupName as string),
  );
  let n = 1;
  while (used.has(`Choice ${n}`)) n += 1;
  return `Choice ${n}`;
}

/** Default field sizes in PDF points — chosen from what the major
 *  platforms render (a signature line ~3in × 0.4in) and sized so the
 *  tap target stays comfortable on the signing page. */
export const DEFAULT_FIELD_SIZE: Record<EsignFieldType, { width: number; height: number }> = {
  signature: { width: 180, height: 34 },
  initials: { width: 56, height: 28 },
  date_signed: { width: 90, height: 26 },
  text: { width: 150, height: 24 },
  checkbox: { width: 18, height: 18 },
  // A pick-one list needs room for its longest choice; a radio button
  // is a checkbox by another name; a picked date is a stamped date's
  // width.
  dropdown: { width: 150, height: 24 },
  radio: { width: 18, height: 18 },
  editable_date: { width: 90, height: 26 },
  // Wide enough to read "Upload a file" — it is a button to the signer,
  // not a box to write in.
  attachment: { width: 160, height: 28 },
};

export const FIELD_TYPE_LABEL: Record<EsignFieldType, string> = {
  signature: "Signature",
  initials: "Initials",
  date_signed: "Date signed",
  text: "Text",
  checkbox: "Checkbox",
  // Named for what the person placing them is trying to build, not for
  // the control's technical name — "Dropdown" and "Radio button" mean
  // nothing to Joy; "Pick one" and "Choose one" do.
  dropdown: "Pick one",
  radio: "Choose one",
  editable_date: "Date they pick",
  attachment: "File upload",
};

/** The same, short enough for a narrow box on the page (Grant,
 *  2026-10-14: a date box that said only "Daniel" didn't say it was a
 *  date — the colour already says whose it is, so the words say what). */
export const FIELD_TYPE_SHORT_LABEL: Record<EsignFieldType, string> = {
  signature: "Signature",
  initials: "Initials",
  date_signed: "Date",
  text: "Text",
  checkbox: "Check",
  dropdown: "Pick one",
  radio: "Choose",
  editable_date: "Date",
  attachment: "File",
};

/** Screen px → PDF points for a page displayed at `displayScale`
 *  (displayedWidthPx / pageWidthPt). */
export function screenToPt(px: number, displayScale: number): number {
  if (displayScale <= 0) return 0;
  return px / displayScale;
}

/** PDF points → screen px. */
export function ptToScreen(pt: number, displayScale: number): number {
  return pt * displayScale;
}

/** True when a field's box doesn't sit fully on its own page — off one
 *  edge, or on a page nobody rendered for it. Every path that creates
 *  or moves a field clamps (see clampToPage below), so this is meant to
 *  stay false in practice; it's the honest backstop the Send gate reads
 *  rather than a routine state. Shared by the single-document check and
 *  the whole-package one (2026-09-18) so the two can't drift apart. */
export function isFieldOffPage(
  field: { pageNumber: number; x: number; y: number; width: number; height: number },
  pages: readonly { pageNumber: number; widthPt: number; heightPt: number }[],
): boolean {
  const page = pages.find((p) => p.pageNumber === field.pageNumber);
  if (!page) return true;
  return (
    field.x < 0 ||
    field.y < 0 ||
    field.x + field.width > page.widthPt ||
    field.y + field.height > page.heightPt
  );
}

type PageSize = { pageNumber: number; widthPt: number; heightPt: number };
type OffPageable = { pageNumber: number; x: number; y: number; width: number; height: number };

/** Every document's field count, off-page fields, fields blocking a
 *  send outright, and unfilled record bindings — summed across a WHOLE
 *  package rather than the one document on screen. This is what should
 *  actually decide whether Send is allowed.
 *
 *  FIXES 2026-09-18 (Baker/Joey): a 7-document mortgage package's
 *  welcome letter has nothing to sign, correctly — the same thing a
 *  prequal letter or a disclosure-only attachment has. But Send refused
 *  the whole package with "place at least one field" because the
 *  letter happened to be the open tab, and the OLD check only ever
 *  looked at the tab on screen. Every other document was fully laid
 *  out. Baker placed a field on the letter that had no business being
 *  there just to unstick the button; the borrower then saw it as a
 *  random highlighted box on a cover letter. A document with nothing
 *  WRONG on it — including nothing PLACED, which is not wrong — must
 *  never be why Send stays gray while the rest of the package is ready.
 *  The same scope mistake applied to off-page fields, unconfigured
 *  fields, and unfilled merge fields: a problem on a document nobody
 *  was viewing didn't stop the send at all.
 *
 *  `isBlocking` and `unfilledMergeFieldsOf` are handed in rather than
 *  hard-coded because they need context this function doesn't have —
 *  the send-readiness rule table and the record's live merge values —
 *  so this function does exactly one job: sum the same three checks
 *  the SAME way across every document, once, so the screen and a test
 *  can't drift apart the way the single-document version did. */
export function computePackageBlockers<TDoc extends { documentId: string; filename: string }, TField extends OffPageable>(
  docs: readonly { doc: TDoc; fields: readonly TField[]; pages: readonly PageSize[] | null }[],
  isBlocking: (field: TField) => boolean,
  unfilledMergeFieldsOf: (fields: readonly TField[]) => readonly TField[],
): {
  totalFieldCount: number;
  offPageByDoc: { doc: TDoc; fields: TField[] }[];
  blockingByDoc: { doc: TDoc; fields: TField[] }[];
  unfilledMergeByDoc: { doc: TDoc; fields: TField[] }[];
} {
  let totalFieldCount = 0;
  const offPageByDoc: { doc: TDoc; fields: TField[] }[] = [];
  const blockingByDoc: { doc: TDoc; fields: TField[] }[] = [];
  const unfilledMergeByDoc: { doc: TDoc; fields: TField[] }[] = [];
  for (const { doc, fields, pages } of docs) {
    totalFieldCount += fields.length;
    // A document nobody has opened yet has no rendered pages, so it has
    // nothing to be measured off the page — the caller's own
    // "documents never opened" check stops the send over that
    // separately.
    if (pages) {
      const off = fields.filter((f) => isFieldOffPage(f, pages));
      if (off.length > 0) offPageByDoc.push({ doc, fields: off });
    }
    const blocking = fields.filter(isBlocking);
    if (blocking.length > 0) blockingByDoc.push({ doc, fields: blocking });
    const unfilled = unfilledMergeFieldsOf(fields);
    if (unfilled.length > 0) unfilledMergeByDoc.push({ doc, fields: [...unfilled] });
  }
  return { totalFieldCount, offPageByDoc, blockingByDoc, unfilledMergeByDoc };
}

/** Clamps a field rect fully onto its page, preserving size where it
 *  fits (a field larger than the page is clamped to the page). */
export function clampToPage(
  rect: { x: number; y: number; width: number; height: number },
  page: { widthPt: number; heightPt: number },
): { x: number; y: number; width: number; height: number } {
  const width = Math.min(rect.width, page.widthPt);
  const height = Math.min(rect.height, page.heightPt);
  const x = Math.min(Math.max(0, rect.x), page.widthPt - width);
  const y = Math.min(Math.max(0, rect.y), page.heightPt - height);
  return { x, y, width, height };
}

/** Fields whose box overlaps a rectangle at all, on one page — the
 *  "drag a selection box around a group" gesture (Joey, 2026-09-18: he
 *  had to uncheck "Required" on every box of a disclosure form one at
 *  a time). Same "any overlap" rule as the click-to-place clamp above
 *  uses for staying on the page, so a field the rectangle only grazes
 *  is still picked up — matching what the rectangle visibly touched is
 *  more honest than requiring the box be fully enclosed. */
export function fieldsInRect(
  fields: readonly PrepField[],
  rect: { pageNumber: number; x: number; y: number; width: number; height: number },
): PrepField[] {
  return fields.filter(
    (f) =>
      f.pageNumber === rect.pageNumber &&
      f.x < rect.x + rect.width &&
      f.x + f.width > rect.x &&
      f.y < rect.y + rect.height &&
      f.y + f.height > rect.y,
  );
}

/** Tri-state for a bulk "Required to finish signing" checkbox over a
 *  multi-selection: every applicable field already required, none are,
 *  or a mix — "mixed" is what a native checkbox's `indeterminate` state
 *  means. A locked (preparer) box has no such setting and is left out
 *  of the count, same as the single-field editor hides the checkbox
 *  for one; "none-applicable" is a selection made up entirely of
 *  locked boxes, where the bulk control has nothing to act on. */
export function requiredTriState(
  fields: readonly PrepField[],
): "all" | "none" | "mixed" | "none-applicable" {
  const applicable = fields.filter((f) => !isPreparerField(f));
  if (applicable.length === 0) return "none-applicable";
  const requiredCount = applicable.filter((f) => f.required).length;
  if (requiredCount === 0) return "none";
  if (requiredCount === applicable.length) return "all";
  return "mixed";
}

/** Centers a default-sized field of `fieldType` on a click point (PDF
 *  points), clamped onto the page. */
/** The box fixed text starts in while a form is being mapped: five lines of
 *  12pt rule, wide enough for an address or a wire-instruction line. */
export const FIXED_TEXT_LINE_PITCH_PT = 12;
export const FIXED_TEXT_SIZE = { width: 260, height: FIXED_TEXT_LINE_PITCH_PT * 5 } as const;

export function placeAtPoint(
  fieldType: EsignFieldType,
  clickPt: { x: number; y: number },
  page: { widthPt: number; heightPt: number },
  sizeOverride?: { width: number; height: number },
): { x: number; y: number; width: number; height: number } {
  const size = sizeOverride ?? DEFAULT_FIELD_SIZE[fieldType];
  return clampToPage(
    {
      x: clickPt.x - size.width / 2,
      y: clickPt.y - size.height / 2,
      width: size.width,
      height: size.height,
    },
    page,
  );
}

/** A strike's box: wide and one line tall around the click, so a click on a
 *  line of printed text covers that line (PR 4a). The sender then drags the
 *  ends to the words they mean. */
export const STRIKE_DEFAULT_SIZE = { width: 220, height: 12 } as const;

export function strikeRect(
  placed: { x: number; y: number; width: number; height: number },
  page: { widthPt: number; heightPt: number },
): { x: number; y: number; width: number; height: number } {
  const cx = placed.x + placed.width / 2;
  const cy = placed.y + placed.height / 2;
  return clampToPage(
    {
      x: cx - STRIKE_DEFAULT_SIZE.width / 2,
      y: cy - STRIKE_DEFAULT_SIZE.height / 2,
      width: STRIKE_DEFAULT_SIZE.width,
      height: STRIKE_DEFAULT_SIZE.height,
    },
    page,
  );
}

/** Arrow-key nudge distance in PDF points (≈1/12 inch; Shift = 3×). */
export const NUDGE_PT = 6;

/** "Repeat on every page": clones a field onto every OTHER page at the
 *  same position, clamped per page (pages can differ in size). Returns
 *  only the NEW fields. */
export function repeatOnEveryPage(
  field: PrepField,
  pages: ReadonlyArray<{ pageNumber: number; widthPt: number; heightPt: number }>,
  makeKey: () => string,
): PrepField[] {
  const clones: PrepField[] = [];
  for (const page of pages) {
    if (page.pageNumber === field.pageNumber) continue;
    const rect = clampToPage(field, page);
    clones.push({ ...field, ...rect, key: makeKey(), pageNumber: page.pageNumber });
  }
  return clones;
}

/** A signer slot in mapping mode ("Map fields" on a blank template —
 *  no real record, so there is nobody to pick from a household). */
export interface MappingSlot {
  id: string;
  label: string;
  /** The catalog seat this slot stands for, when the mapper has named
   *  one (2026-09-15). A form taught with named seats places its fields
   *  on whoever holds that seat on every future send; a form taught
   *  with plain numbered slots keeps the positional behaviour. */
  roleKey?: string | null;
}

/**
 * SHARE THE SIGNATURE BLOCKS OUT (Grant, 2026-10-13). A form with
 * several identical signature blocks — AAR's addenda and counter offers
 * print "Seller Buyer Landlord Tenant" under every one — names nobody
 * on any of them, and every one used to land on the first signer:
 * Daniel got all four, Amelia none.
 *
 * Signature and date marks nobody claimed are grouped into blocks (a
 * signature and the date to its right on the same line), put in the
 * order the page reads, and dealt round the people signing this send in
 * seat order: block 1 to Seller 1, block 2 to Seller 2, block 3 to
 * Seller 1 again. A date always goes with its signature. Returns the
 * signer key for each mark, by index; marks it was not given (initials,
 * anything else) are left out of the map.
 *
 * Initials are deliberately not dealt: a form that asks for initials
 * asks every signer for them, and splitting them would leave half the
 * pages unanswered by each person.
 */
export function distributeSigningBlocks(
  marks: readonly {
    fieldType: EsignFieldType;
    pageNumber: number;
    x: number;
    y: number;
    width: number;
    height: number;
  }[],
  signerKeys: readonly string[],
): Map<number, string> {
  const out = new Map<number, string>();
  if (signerKeys.length === 0) return out;
  const items = marks
    .map((m, index) => ({ m, index, bottom: m.y + m.height }))
    .filter(({ m }) => m.fieldType === "signature" || m.fieldType === "date_signed");
  // Rows: marks whose bottoms (their rule) are within a few points.
  items.sort((a, b) => a.m.pageNumber - b.m.pageNumber || a.bottom - b.bottom || a.m.x - b.m.x);
  const rows: (typeof items)[] = [];
  for (const it of items) {
    const row = rows[rows.length - 1];
    if (row && row[0]!.m.pageNumber === it.m.pageNumber && Math.abs(row[0]!.bottom - it.bottom) <= 8) {
      row.push(it);
    } else {
      rows.push([it]);
    }
  }
  let block = 0;
  for (const row of rows) {
    const sorted = [...row].sort((a, b) => a.m.x - b.m.x);
    const used = new Set<number>();
    for (const it of sorted) {
      if (used.has(it.index)) continue;
      const key = signerKeys[block % signerKeys.length]!;
      out.set(it.index, key);
      used.add(it.index);
      if (it.m.fieldType === "signature") {
        // Its date: the nearest date to the right on this row, before
        // the next signature.
        const nextSig = sorted.find((o) => o.m.fieldType === "signature" && o.m.x > it.m.x);
        const date = sorted.find(
          (o) =>
            o.m.fieldType === "date_signed" &&
            !used.has(o.index) &&
            o.m.x >= it.m.x &&
            (!nextSig || o.m.x < nextSig.m.x),
        );
        if (date) {
          out.set(date.index, key);
          used.add(date.index);
        }
      }
      block += 1;
    }
  }
  return out;
}

/** Turn Stew's per-field role hints ("Owner", "Agent", "Broker") into
 *  the mapping panel's own signer slots.
 *
 *  Nate, on the AAR Residential Listing Contract: the panel opened with
 *  two generic slots, "Signer 1" and "Signer 2," even though the form
 *  has two Owner signature columns, two Agent columns, and a broker
 *  block — five distinct roles a two-slot generic default can't name.
 *  Every suggested field already carries a `signerHint` naming the role
 *  the document itself uses; this turns THAT into the slot labels, so
 *  the panel reads what the document says instead of a placeholder
 *  count.
 *
 *  Deduplicated case-insensitively, in the order each role is first
 *  seen (roughly reading order down the document), capped at
 *  `maxSlots`. Two columns sharing one role word (two Owner columns,
 *  both hinted "Owner") collapse to ONE slot here — the hint alone
 *  can't tell two owners apart, and guessing a split would risk
 *  putting a stranger's fields under the wrong name. Add/remove stays
 *  the advisor's tool for exactly that case: one click adds a second
 *  "Owner" slot to rename.
 *
 *  Returns an empty list when nothing carried a hint (a scan, a
 *  document Stew couldn't read, or a form with no role words at all)
 *  so the caller can keep its generic default rather than replacing it
 *  with nothing. */
export function slotsFromSignerHints(
  hints: readonly (string | null | undefined)[],
  maxSlots: number,
  /** This company's seats, when the caller knows them. A hint that
   *  names one ("Buyer", "Co-borrower") adopts that seat AND its
   *  catalog label, so the slot the mapper sees is the same seat the
   *  send will cast. A hint that names none stays a plain named slot —
   *  the form's own word is still better than "Signer 2". */
  catalog: readonly SigningRole[] = [],
): MappingSlot[] {
  const seen = new Set<string>();
  const slots: MappingSlot[] = [];
  const takenRoles = new Set<string>();
  for (const raw of hints) {
    const label = (raw ?? "").trim();
    if (!label) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    // Two hints that name the SAME seat are one slot, not two: "Buyer"
    // and "Buyer's Signature" are the same person signing twice.
    const role = catalog.length > 0 ? matchCaptionToRole(label, catalog) : null;
    if (role && takenRoles.has(role.key)) continue;
    if (role) takenRoles.add(role.key);
    // The seat is attached ONLY when one matched. A slot with no seat
    // keeps the exact shape it has always had — no `roleKey` property
    // at all — because callers and tests compare these objects whole,
    // and an always-present null is a different object.
    slots.push(
      role
        ? { id: `slot-${slots.length}`, label: role.label, roleKey: role.key }
        : { id: `slot-${slots.length}`, label },
    );
    if (slots.length >= maxSlots) break;
  }
  return slots;
}

/** Move one entry of a list to a new position, returning a new list.
 *
 *  Extracted from the Prepare & Send rail (Nate, 2026-09-02: "I want
 *  the ability to drag the files on the left to change their order
 *  prior to sending") so the arithmetic that decides what a client
 *  signs first can be exercised without booting a PDF renderer — the
 *  screen itself cannot be rendered in a test, since pdf.js touches
 *  DOMMatrix at import time.
 *
 *  Out-of-range indices return the list unchanged rather than throwing:
 *  a reorder that cannot be expressed must leave the envelope exactly
 *  as it was, never half-applied.
 */
/** "page 4" / "pages 2, 5 and 9", or "" when there are none.
 *
 *  Every blocker on the send screen names where to look. The lesson was
 *  paid for in staging round 3 (2026-08-23): a refusal that said "one
 *  of the placed fields" sent someone scrolling an eleven-page
 *  contract. Shared by every blocker so a new one can't quietly skip
 *  it. */
export function describePages(items: readonly { pageNumber: number }[]): string {
  const pages = [...new Set(items.map((f) => f.pageNumber))].sort((a, b) => a - b);
  if (pages.length === 0) return "";
  if (pages.length === 1) return `page ${pages[0]}`;
  return `pages ${pages.slice(0, -1).join(", ")} and ${pages[pages.length - 1]}`;
}

/** A package-wide blocker note that names which OTHER document (not the
 *  one on screen) still has the problem.
 *
 *  Fixes the 2026-09-18 Baker/Joey bug: a 7-document mortgage package's
 *  welcome letter has nothing to sign, correctly, but Send refused it
 *  anyway because the letter happened to be the open tab and the gate
 *  only ever checked the tab on screen — not the package. Every problem
 *  this screen checks (nothing placed, a field off the page, an
 *  unconfigured field, an unfilled record binding) has this same scope
 *  mistake available to it: a problem on a document nobody is viewing
 *  must still stop the send, and this is how the screen says so. The
 *  document the sender IS looking at keeps its own plain note
 *  (built separately, without this function) so the two never say the
 *  same thing twice. */
export function otherDocumentBlockerNote(
  byDoc: readonly { doc: { documentId: string; filename: string }; fields: readonly { pageNumber: number }[] }[],
  activeDocumentId: string,
  /** "2 fields are off the page" — everything except the page numbers
   *  and the document name, which this function adds. */
  describe: (fields: readonly { pageNumber: number }[]) => string,
): string | null {
  const others = byDoc.filter((o) => o.doc.documentId !== activeDocumentId);
  if (others.length === 0) return null;
  const parts = others.map(
    (o) => `${describe(o.fields)} on "${o.doc.filename}" (${describePages(o.fields)})`,
  );
  return `${parts.join("; ")}. Open ${
    others.length === 1 ? "that document" : "those documents"
  } to fix ${others.length === 1 ? "it" : "them"} before sending.`;
}

export function moveInList<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  if (from === to) return next;
  if (from < 0 || to < 0 || from >= next.length || to >= next.length) return next;
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return [...items];
  next.splice(to, 0, moved);
  return next;
}

// ── Signers who are not one of our contacts ─────────────────────────
//
// A guest signer (a co-op agent, a seller, a title officer — Grant,
// 2026-09-03) has no contact id to be identified by, so the prep screen
// gives them a key derived from their POSITION in its own guest list:
// `guest:0`, `guest:1`, and so on.
//
// Position-derived keys are cheap and match what the server records,
// but they have one sharp edge: removing a guest renumbers every guest
// after them. If the fields already placed on the page don't move with
// that renumbering, the title officer inherits the co-op agent's
// signature line — silently, on a document that still looks finished.
//
// That remap is why this is a tested function rather than a filter
// written inline.

/** The prefix a guest signer's key carries. */
export const GUEST_SIGNER_PREFIX = "guest:";

export function guestSignerKey(index: number): string {
  return `${GUEST_SIGNER_PREFIX}${index}`;
}

export function isGuestSignerKey(key: string): boolean {
  return key.startsWith(GUEST_SIGNER_PREFIX);
}

/** The index inside the guest list, or null when this isn't a guest key.
 *
 *  Matched against digits rather than handed to `Number`, which reads
 *  "guest:" as 0 (empty string coerces to zero) — so a malformed key
 *  would have claimed the FIRST guest's identity and, with it, their
 *  signature fields. */
export function guestSignerIndex(key: string): number | null {
  if (!isGuestSignerKey(key)) return null;
  const rest = key.slice(GUEST_SIGNER_PREFIX.length);
  if (!/^\d+$/.test(rest)) return null;
  return Number(rest);
}

/**
 * Where a signer key lands once the guest at `removedIndex` is gone.
 *
 * A contact's key never moves — it isn't positional. A guest before the
 * removed one keeps their number. A guest AFTER them shifts down by
 * one, which is the whole point.
 *
 * The removed guest's own key is returned unchanged; callers drop those
 * rows rather than remapping them, and returning a neighbour's key here
 * would be the misattribution this function exists to prevent.
 */
export function remapAfterGuestRemoval(key: string, removedIndex: number): string {
  const i = guestSignerIndex(key);
  if (i === null || i <= removedIndex) return key;
  return guestSignerKey(i - 1);
}

/** Advisor self-signing (Grant, 2026-08-22): a spot on the document
 *  where the SENDER's own saved signature gets stamped in directly —
 *  no signer, no BoldSign field, applied server-side before the send. */
export interface PrepStamp {
  key: string;
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  /** True when the OS placed this over one of the sender's own
   *  signature lines, from a map that knows its seats. Only these are
   *  cleared and re-placed when the suggestion pass runs again; a stamp
   *  the advisor dropped by hand is theirs and stays where they put it. */
  fromSeat?: boolean;
}

/**
 * Whether a box placed while MAPPING a form is a record value the OS fills,
 * owned by nobody, rather than the signer "Placing for" names (Grant,
 * 2026-10-27). Nate mapped the Commission Disbursement Authorization with
 * Buyer 1 selected, so escrow company, price and commission were saved as
 * Buyer 1's boxes and dropped from every listing send, where nobody is
 * Buyer 1. A record-level value is never typed by a signer. A token about
 * one PERSON (their printed name, email) still goes to that signer.
 */
export function isMappedRecordValue(input: {
  mapping: boolean;
  placingLocked: boolean;
  placingType: string | null;
  mergeToken: string | null;
  isPersonScoped: (token: string) => boolean;
}): boolean {
  return (
    input.mapping &&
    !input.placingLocked &&
    input.placingType === "text" &&
    input.mergeToken !== null &&
    !input.isPersonScoped(input.mergeToken)
  );
}

// ── Signing order, by dragging (Nate, 2026-11-03) ─────────────────────
//
// "When the box is checked for a signing order, I don't want numbers ...
// but rather want to drag them in the order. The numbers can remain on the
// left, indicating who will get the esign first, second, third."
//
// DECISION: with an order set, everyone has their own number, 1 to n, in
// the order the rows are dragged into. The earlier design let two people
// share a number ("same number signs together"); that was dropped for the
// simpler mental model Nate asked for. A send where everyone signs at once
// is still one click: leave "Set a signing order" unticked.

/** The signers in signing order: by their number, ties and the unnumbered
 *  kept in the order they are listed. */
export function signersInOrder(
  keys: readonly string[],
  orderByKey: Readonly<Record<string, number>>,
): string[] {
  return keys
    .map((key, i) => ({ key, i, n: orderByKey[key] ?? Number.POSITIVE_INFINITY }))
    .sort((a, b) => a.n - b.n || a.i - b.i)
    .map((x) => x.key);
}

/** Numbers 1 to n for the signers in their current order, or null when
 *  they already have exactly those numbers (so a caller can skip a state
 *  update that would change nothing). A newly added signer, who has no
 *  number yet, lands last. */
export function sequentialSigningOrder(
  keys: readonly string[],
  orderByKey: Readonly<Record<string, number>>,
): Record<string, number> | null {
  const ordered = signersInOrder(keys, orderByKey);
  const next: Record<string, number> = {};
  ordered.forEach((key, i) => {
    next[key] = i + 1;
  });
  return ordered.every((key) => orderByKey[key] === next[key]) ? null : next;
}

/** The order after dragging `key` to sit at `toIndex` (0 = signs first).
 *  An unknown key, or a drop where it already sits, returns the order as
 *  it was. */
export function moveSignerTo(ordered: readonly string[], key: string, toIndex: number): string[] {
  const from = ordered.indexOf(key);
  if (from === -1) return [...ordered];
  const rest = ordered.filter((k) => k !== key);
  const at = Math.max(0, Math.min(toIndex, rest.length));
  return [...rest.slice(0, at), key, ...rest.slice(at)];
}
